import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Graphe } from '../Services/EntiteProjet';
import {
  SharedTimeInterval,
  createChartRoot,
  createDateAxis,
  createToggleLegend,
  createValueAxis,
  createXYChart,
  createXYCursor,
} from './amcharts-xy';

export type GrapheDataPoint = { date: number } & Record<string, number>;

/**
 * Mappe graphe.interval → baseInterval amCharts.
 * "minute" → données à la minute (mode jour)
 * "jour"   → données journalières (mode mois)
 * "mois"   → données mensuelles (mode annee)
 */
export function intervalVersBaseInterval(interval: string): SharedTimeInterval {
  switch (interval) {
    case 'mois': return { timeUnit: 'month', count: 1 };
    case 'jour': return { timeUnit: 'day', count: 1 };
    default:     return { timeUnit: 'minute', count: 1 };
  }
}

export function createGrapheChart(
  host: HTMLDivElement,
  graphe: Graphe,
  data: GrapheDataPoint[],
): am5.Root {
  const root = createChartRoot(host);
  const chart = createXYChart(root, {
    enableHorizontalZoom: true,
    showAllSeriesTooltips: true,
    paddingBottom: 8,
  });

  const xAxis = createDateAxis(root, chart, {
    baseInterval: intervalVersBaseInterval(graphe.interval),
  });

  const yAxisGauche = createValueAxis(root, chart);
  const aAxeDroite = graphe.series.some(s => s.axe === 3);
  const yAxisDroite = aAxeDroite ? createValueAxis(root, chart, { opposite: true }) : null;

  const series: am5xy.LineSeries[] = [];

  for (const serie of graphe.series) {
    if (serie.axe === 1) continue; // axe X d'un scatter — non géré
    const yAxis = (serie.axe === 3 && yAxisDroite) ? yAxisDroite : yAxisGauche;
    const couleur = parseInt(serie.couleur, 16);

    const s = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: serie.point_mesure.libelle ?? serie.point_mesure.nom,
        xAxis,
        yAxis,
        valueXField: 'date',
        valueYField: serie.point_mesure.nom,
        stroke: am5.color(couleur),
        fill: am5.color(couleur),
        maskBullets: false,
        snapTooltip: true,
      })
    );

    s.strokes.template.setAll({ strokeWidth: 2 });
    s.setAll({
      tooltip: am5.Tooltip.new(root, {
        labelText: `{name}\n{valueX.formatDate('dd/MM/yyyy HH:mm')}\n{valueY.formatNumber('#,###.##')}`,
      }),
    });
    s.data.setAll(data);
    series.push(s);
  }

  createXYCursor(root, chart, { snapToSeries: series, showHorizontalLine: false });
  if (series.length > 1) createToggleLegend(root, chart, series);
  chart.appear(600, 100);
  return root;
}
